import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// HubSpot tracking loader. Pattern mirrors `AskDDLoader.jsx`:
//   • Lazy-injected on `requestIdleCallback` so it never blocks LCP / INP.
//   • Singleton — idempotent against React.StrictMode double-effects and
//     unmount/remount cycles.
//   • Returns null; mounted once at the Layout level.
//
// Additionally, HubSpot's tracker does NOT auto-detect React Router
// navigations. We watch `useLocation` and push `setPath` + `trackPageView`
// to the `_hsq` queue on every route change so the dashboard records
// real per-route page views, not just the first landing.

const HUBSPOT_PORTAL_ID = '442408370';
const HUBSPOT_SCRIPT_SRC = `https://js-ap1.hs-scripts.com/${HUBSPOT_PORTAL_ID}.js`;

function injectHubSpot() {
  if (typeof window === 'undefined') return;
  // Pre-create the queue so any `_hsq.push(...)` calls made before the
  // loader script lands are replayed once it does.
  window._hsq = window._hsq || [];

  if (document.getElementById('hs-script-loader')) return;
  if (document.querySelector(`script[src="${HUBSPOT_SCRIPT_SRC}"]`)) return;

  const script = document.createElement('script');
  script.id = 'hs-script-loader';
  script.type = 'text/javascript';
  script.async = true;
  script.defer = true;
  script.src = HUBSPOT_SCRIPT_SRC;
  document.body.appendChild(script);
}

export default function HubSpotLoader() {
  const { pathname, search } = useLocation();

  // One-time lazy injection on the first mount.
  useEffect(() => {
    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      window.requestIdleCallback(injectHubSpot, { timeout: 4000 });
    } else {
      setTimeout(injectHubSpot, 2500);
    }
  }, []);

  // Per-route page-view tracking. We push to `_hsq` even before the
  // loader script lands — HubSpot drains the queue on init.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window._hsq = window._hsq || [];
    const path = pathname + (search || '');
    window._hsq.push(['setPath', path]);
    window._hsq.push(['trackPageView']);
  }, [pathname, search]);

  return null;
}
